import { serializer } from '@wix/metro-runtime/ambassador';
import { resolveUrl } from '@wix/metro-runtime';
var _address = { geocode: '_addressLocation' };
var _addressLocation = {
    latitude: 'google.protobuf.DoubleValue',
    longitude: 'google.protobuf.DoubleValue',
};
var _booking = {
    createdDate: 'google.protobuf.Timestamp',
    startDate: 'google.protobuf.Timestamp',
    endDate: 'google.protobuf.Timestamp',
    updatedDate: 'google.protobuf.Timestamp',
    canceledDate: 'google.protobuf.Timestamp',
    contactDetails: '_contactDetails',
};
var _bookingLineItem = { pricePerParticipant: 'google.protobuf.DoubleValue' };
var _calculatePriceRequest = { booking: '_booking' };
var _calculatePriceResponse = { priceInfo: '_priceInfo' };
var _contactDetails = { fullAddress: '_address' };
var _previewPriceRequest = { bookingLineItems: '_bookingLineItem' };
var _previewPriceResponse = { priceInfo: '_priceInfo' };
var _priceInfo = {
    calculatedPrice: 'DOUBLE',
    deposit: 'google.protobuf.DoubleValue',
    bookingLineItems: '_bookingLineItem',
};
function resolveComWixpressBookingsPricingBookingsPricingServiceUrl(opts) {
    var domainToMappings = {
        'manage._base_domain_': [
            {
                srcPath: '/_api/bookings-pricing',
                destPath: '',
            },
            {
                srcPath: '/_api/bookings-pricing/v2/price',
                destPath: '/v2/pricing',
            },
        ],
        _: [
            {
                srcPath: '/_api/bookings-pricing',
                destPath: '',
            },
            {
                srcPath: '/_api/bookings-pricing/v2/price',
                destPath: '/v2/pricing',
            },
        ],
        'www.wixapis.com': [
            {
                srcPath: '/bookings/v2/pricing/v2/pricing',
                destPath: '/v2/pricing',
            },
            {
                srcPath: '/bookings/v2/pricing',
                destPath: '/v2/pricing',
            },
        ],
    };
    return resolveUrl(Object.assign(opts, { domainToMappings: domainToMappings }));
}
/**
 * Previews the base price for a set of line items belonging to the same service,
 * before a booking is actually created.
 *
 *
 *
 * The returned price preview information about each line
 * item's price and sums up each line item's price.
 *
 * The previewed price is not the actual price that will be used to charge the customer.
 * `Preview Price` only estimates the base price by adding up the price
 * of each line item before
 * the booking is actually created.
 *
 * Use [`Calculate Price`](https://dev.wix.com/api/rest/wix-bookings/pricing/calculate-price) to get the base price
 * after the booking is created.
 *
 * Passing line items that belong to different services results in an error.
 *
 * ## Calculating the previewed price
 *
 * Wix Bookings has its own default pricing logic for previewing the price. You must
 * pass the `serviceId` in the `slot`
 * or `schedule` object to [`Preview Price`](https://dev.wix.com/api/rest/wix-bookings/pricing/preview-price).
 *
 * You cannot call `Preview Price` if you have customized Bookings pricing logic using the
 * [Bookings Pricing Integration SPI](https://dev.wix.com/api/rest/wix-bookings/pricing-provider). Calling `Preview Price` if custom pricing
 * logic has been implemented for the site results in an error.
 */
export function previewPrice(payload) {
    var _a = serializer(_previewPriceRequest, { _bookingLineItem: _bookingLineItem }), toReq = _a.toJSON, fromReq = _a.fromJSON;
    var fromRes = serializer(_previewPriceResponse, {
        _bookingLineItem: _bookingLineItem,
        _priceInfo: _priceInfo,
    }).fromJSON;
    function __previewPrice(_a) {
        var host = _a.host;
        var serializedData = toReq(payload);
        var metadata = {
            entityFqdn: 'wix.bookings.v2.price_info',
            method: 'POST',
            methodFqn: 'com.wixpress.bookings.pricing.BookingsPricingService.PreviewPrice',
            url: resolveComWixpressBookingsPricingBookingsPricingServiceUrl({
                protoPath: '/v2/pricing/preview',
                data: serializedData,
                host: host,
            }),
            data: serializedData,
            transformResponse: fromRes,
        };
        return metadata;
    }
    __previewPrice.fromReq = fromReq;
    __previewPrice.__isAmbassador = true;
    return __previewPrice;
}
/**
 * Calculates the base price of a booking.
 *
 *
 *
 * You can call [`Calculate Price`](https://dev.wix.com/api/rest/wix-bookings/pricing/calculate-price) after a booking is created. The returned calculated price includes information about each line
 * item's price and the booking's total price.
 *
 * You can use [`Preview Price`](https://dev.wix.com/api/rest/wix-bookings/pricing/preview-price) to get the base price
 * before a booking is created.
 *
 * The calculated price is the base price that will be used as a basis for charging the customer.
 * During checkout, additional taxes
 * and fees might be added to this base price.
 *
 * ## Calculating the price
 *
 * Wix Bookings has its own default pricing logic for calculating the price. When
 * using Wix Bookings' default pricing logic, you must pass the `serviceId` in the `slot`
 * or `schedule` object to [`Calculate Price`](https://dev.wix.com/api/rest/wix-bookings/pricing/calculate-price).
 *
 * Alternatively you can customize the pricing logic using the
 * [Bookings Pricing Integration SPI](https://dev.wix.com/api/rest/wix-bookings/pricing-provider).
 * If you integrate with a pricing provider, the customized pricing logic becomes
 * the default logic. This means that, if the Pricing Integration SPI is implemented,
 * when calling the API's `Calculate Price` endpoint,
 * the [SPI's `Calculate Price` endpoint](https://dev.wix.com/api/rest/wix-bookings/pricing/calculate-price) is used instead.
 */
export function calculatePrice(payload) {
    var _a = serializer(_calculatePriceRequest, { _address: _address, _addressLocation: _addressLocation, _booking: _booking, _contactDetails: _contactDetails }), toReq = _a.toJSON, fromReq = _a.fromJSON;
    var fromRes = serializer(_calculatePriceResponse, {
        _bookingLineItem: _bookingLineItem,
        _priceInfo: _priceInfo,
    }).fromJSON;
    function __calculatePrice(_a) {
        var host = _a.host;
        var serializedData = toReq(payload);
        var metadata = {
            entityFqdn: 'wix.bookings.v2.price_info',
            method: 'POST',
            methodFqn: 'com.wixpress.bookings.pricing.BookingsPricingService.CalculatePrice',
            url: resolveComWixpressBookingsPricingBookingsPricingServiceUrl({
                protoPath: '/v2/pricing/calculate',
                data: serializedData,
                host: host,
            }),
            data: serializedData,
            transformResponse: fromRes,
        };
        return metadata;
    }
    __calculatePrice.fromReq = fromReq;
    __calculatePrice.__isAmbassador = true;
    return __calculatePrice;
}
//# sourceMappingURL=http.impl.js.map